/**
 * Form Viewer preview protocol.
 *
 * The admin webapp's Form Viewer embeds this app in an iframe so it can show
 * how a section really renders on a device. It embeds the REAL app rather than
 * re-drawing the question UI in HTML, because a second renderer drifts: the
 * training previews under `apps/admin/public/training-previews/` are exactly
 * that, and nothing catches it when they diverge from
 * `apps/mobile/components/training/screens/`.
 *
 * The two sides talk over postMessage rather than by poking at the iframe's
 * document, because in local development they are on different origins (admin
 * on the slot's Next port, Expo on the slot's Metro port) and only postMessage
 * crosses that boundary.
 *
 * This module is the contract. It holds no React and no side effects so both
 * sides can import the types and neither can drift from the other.
 */

/** Query flag that switches the app into preview mode. See `app/_layout.tsx`. */
export const FORM_PREVIEW_QUERY_FLAG = "formPreview";

/**
 * A question exactly as the API hands it to the device.
 *
 * Structurally the mobile `Question` from `lib/api.ts`; restated here as the
 * wire shape so the host does not have to import mobile's API module.
 */
export interface FormPreviewQuestion {
    id: string;
    text: string;
    type: string;
    orderIndex: number;
    mandatory: boolean;
    visibleOnMobile: boolean;
    defaultValue: string | null;
    config: Record<string, unknown> | null;
    parentQuestionId: string | null;
    patientDataKey: string | null;
    hintText: string | null;
    answerOptions: {
        id: string;
        questionId: string;
        orderIndex: number;
        text: string;
        value: string;
    }[];
    branchingRules: {
        id: string;
        sourceQuestionId: string;
        sourceAnswerValue: string;
        targetQuestionId: string;
        logicOperator: string;
    }[];
}

/** Host → preview. */
export type FormPreviewInbound =
    | {
          type: "form-preview:load";
          sectionId: string;
          sectionName: string;
          questions: FormPreviewQuestion[];
          /** Seeds answers so a branch can be opened from the catalog side. */
          answers?: Record<string, string>;
      }
    | { type: "form-preview:scroll-to"; questionId: string }
    | { type: "form-preview:reset" };

/** Preview → host. */
export type FormPreviewOutbound =
    | { type: "form-preview:ready" }
    | {
          type: "form-preview:state";
          answers: Record<string, string>;
          /** Ids the branching engine says are visible, in render order. */
          visibleIds: string[];
          /** Ids actually drawn — visibleIds minus hidden computed fields. */
          renderedIds: string[];
          /** What the last change did, so the host can flash the right rows. */
          lastChange: { questionId: string; value: string } | null;
      };

/** True when this page load is an admin form preview rather than the app. */
export function isFormPreviewRequested(search: string | undefined): boolean {
    if (!search) {
        return false;
    }
    try {
        return new URLSearchParams(search).get(FORM_PREVIEW_QUERY_FLAG) === "1";
    } catch {
        // A malformed query string must not take the real app down.
        return false;
    }
}

/** Narrows an untrusted `MessageEvent.data` to a host message. */
export function parseInbound(data: unknown): FormPreviewInbound | null {
    if (typeof data !== "object" || data === null) {
        return null;
    }
    const type = (data as { type?: unknown }).type;
    if (
        type === "form-preview:load" ||
        type === "form-preview:scroll-to" ||
        type === "form-preview:reset"
    ) {
        return data as FormPreviewInbound;
    }
    return null;
}

/** Narrows an untrusted `MessageEvent.data` to a preview message. */
export function parseOutbound(data: unknown): FormPreviewOutbound | null {
    if (typeof data !== "object" || data === null) {
        return null;
    }
    const type = (data as { type?: unknown }).type;
    if (type === "form-preview:ready" || type === "form-preview:state") {
        return data as FormPreviewOutbound;
    }
    return null;
}

/**
 * Hidden computed questions (`config.hidden`) stay in the visibility graph so
 * they can trigger follow-ups, but are never drawn. `QuestionsChapter` applies
 * this same filter after computing visibility — mirrored here so the host can
 * distinguish "visible to the engine" from "on screen" without re-reading
 * every question's config.
 */
export function isDrawn(q: Pick<FormPreviewQuestion, "config" | "visibleOnMobile">): boolean {
    if (q.visibleOnMobile === false) {
        return false;
    }
    return (q.config as { hidden?: boolean } | null)?.hidden !== true;
}
